import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import { useHistory } from 'react-router-dom';
import useNavigationPage from '../../hooks/useNavigationPage';
import useUnitConversionService from '../../app/services/hooks/UseUnitConversionService';
import { UnitConversion, UnitConversionFilters } from '../../app/models/UnitConversion';
import UnitConversionDataTable from './components/tables/UnitConversionDataTable';
import UnitConversionCreate from './components/UnitConversionCreate';
import UnitConversionEdit from './components/UnitConversionEdit';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Conversión de unidades',
        url: '/unit-conversions',
        isActive: true
    }
];

const UnitConversions = () => {
    const { navigationPage } = useNavigationPage();
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { fetchingGetUnitConversions, getUnitConversions, deleteUnitConversion } =
        useUnitConversionService();
    const [UnitConversions, setUnitConversions] = useState<UnitConversion[]>([]);

    const [unitConversionIdEdit, setUnitConversionIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);

    const [filter, setFilter] = useState<UnitConversionFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'asc'
    });

    useEffect(() => {
        reloadTable();
    }, [filter]);

    const getAllUnitConversions = () => {
        getUnitConversions(filter, {
            onSuccess: (response: ServiceResponse) => {
                setUnitConversions(response.data.unit_conversions);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message, {
                    autoClose: 2000
                });

                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (unitConversionId: number) => {
        setShowingEdit(true);
        setUnitConversionIdEdit(unitConversionId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setUnitConversionIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllUnitConversions();
    };

    const destroy = (unitConversionId: number) => {
        const _text = 'Está a punto de eliminar la conversión de unidad #' + unitConversionId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando conversión de unidad...');
                        deleteUnitConversion(unitConversionId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La conversión de unidad no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };


    return (
        <>
            <Breadcrumbs pageSection="Conversión de unidades" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nueva conversión de unidad" />}
                />
                <UnitConversionDataTable
                    unitConversions={UnitConversions}
                    edit={showEdit}
                    destroy={destroy}
                    loading={fetchingGetUnitConversions}
                    filter={filter}
                    setFilter={setFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear conversión de unidad"
                    backdrop={true}
                    showFooter={false}
                >
                    <UnitConversionCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}

                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar conversión de unidad"
                    backdrop={true}
                    showFooter={false}
                >
                    <UnitConversionEdit
                        unitConversionId={unitConversionIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default UnitConversions;
